import { Component, HostListener, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { HomeService } from '../../../../services/home.service';
import { CartService } from '../../../../services/cart';
import { Subject, takeUntil } from 'rxjs';
import { Stockitem } from '../../../../interfaces/stockitem';
import { AccountTypeService } from '../../../../services/account-type';
import { UserService } from '../../../../services/user';

@Component({
  selector: 'app-stockpageclients',
  templateUrl: './stockpageclients.component.html',
  styleUrls: ['./stockpageclients.component.scss']
})
export class StockpageclientsComponent implements OnInit, OnDestroy {

  allData: Stockitem[] = [];

  contentReady = false;
  noData = false;
  searched = false;
  cartPopUp = false;

  searchKey = '';
  cartItemCount = 0;

  page = 1;
  totalItems = 1;
  warehouseId: number | null = null;
  cartItems: any[] = [];

  catagoryId: number | null = 0;

  isAr = localStorage.getItem('language') === 'ar';

  private destroy$ = new Subject<void>();

  constructor(
    private router: Router,
    private homeService: HomeService,
    private userService: UserService,
    private cartService: CartService,
    public accountTypeService: AccountTypeService
  ) { }

  /* ================= INIT ================= */

  ngOnInit(): void {

    this.cartService.cart$
      .pipe(takeUntil(this.destroy$))
      .subscribe(cart => this.cartItems = cart);

    this.cartService.cartCount$
      .pipe(takeUntil(this.destroy$))
      .subscribe(count => this.cartItemCount = count);

    const storedId = localStorage.getItem('posId');
    this.warehouseId = storedId ? Number(storedId) : null;

    this.accountTypeService.getAccountType()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => this.loadInitialData(),
        error: () => this.loadInitialData()
      });
  }

  private loadInitialData(): void {
    if (this.warehouseId) {
      this.getWarehouse(true);
    } else {
      this.noData = true;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /* ================= FILTERS ================= */

  getByCategory(catagoryId: number | null): void {
    this.catagoryId = catagoryId;
    this.resetAndReload();
  }

  private resetAndReload(): void {
    this.searched = false;
    this.searchKey = '';
    this.page = 1;
    this.allData = [];
    this.getWarehouse(true);
  }

  /* ================= LOAD DATA ================= */

  getWarehouse(reset = false): void {

    if (!this.warehouseId) return;
    if (this.contentReady) return;

    if (reset) {
      this.page = 1;
      this.allData = [];
    }

    this.contentReady = true;

    this.homeService.getWarehouseStocks(
      this.warehouseId,
      this.page,
      {
        categoryId: this.catagoryId
      }
    ).subscribe({
      next: (res: any) => {

        // ✅ Laravel بيرجع data مباشرة
        const newData: Stockitem[] = res?.data ?? [];
        this.totalItems = res?.last_page ?? 1;

        this.allData = [...this.allData, ...newData];
        this.noData = this.allData.length === 0;

        this.contentReady = false;
      },
      error: (err: any) => {
        console.error(err);
        this.contentReady = false;
      }
    });
  }

  /* ================= INFINITE SCROLL ================= */

  @HostListener('window:scroll')
  onScroll(): void {

    if (this.contentReady) return;
    if (this.searched) return;
    if (this.page >= this.totalItems) return;

    const threshold = 150;
    const position = window.innerHeight + window.scrollY;
    const height = document.documentElement.scrollHeight;

    if (position >= height - threshold) {
      this.page++;
      this.getWarehouse();
    }
  }

  /* ================= SEARCH ================= */

  search(): void {
    if (!this.searchKey.trim() || !this.warehouseId) return;

    this.contentReady = true;
    this.searched = true;

    this.homeService.searchByNameAndWarehouse(
      this.searchKey,
      this.warehouseId
    ).subscribe({
      next: (res: any) => {
        this.allData = res ?? [];
        this.noData = this.allData.length === 0;
        this.contentReady = false;
      },
      error: (err: any) => {
        console.error(err);
        this.contentReady = false;
      }
    });
  }

  clearSearch(): void {
    this.searchKey = '';
    this.searched = false;
    this.resetAndReload();
  }

  /* ================= CART ================= */

  addToCart(item: Stockitem): void {
    this.cartService.addOrToggleItem(item);
  }

  removeFromCart(item: Stockitem): void {
    this.cartService.removeItem(item);
    if (this.cartService.getCart().length === 0) {
      this.cartPopUp = false;
    }
  }

  isInCart(item: Stockitem): boolean {
    return this.cartService.getCart().some(i => i.id === item.id);
  }

  quickOrder(): void {
    const posId = Number(localStorage.getItem('posId'));

    this.userService.getWarehouseType(posId).subscribe({
      next: ({ warehouses_type }) => {
        const type = Number(warehouses_type);

        if ([2, 3, 4].includes(type)) {
          this.router.navigate(['/Addorderstore'], {
            queryParams: {
              cart: JSON.stringify(this.cartItems)
            }
          });
          return;
        }

        else if (type === 1) {
          this.router.navigate(['/Addorderwarehouse'], {
            queryParams: {
              cart: JSON.stringify(this.cartItems)
            }
          });
          return;
        }

        else {
          this.router.navigate(['/Addorder'], {
            queryParams: {
              cart: JSON.stringify(this.cartItems)
            }
          });
          return;
        }
      },
      error: (err) => console.error(err),
    });
  }

  cartPopUpOpen(): void { this.cartPopUp = true; }
  close(): void { this.cartPopUp = false; }

  /* ================= UTILS ================= */

  fixImage(url: string): string {
    return `https://backendproduction.blueboxpet.com/storage/app/public/${url}`;
  }

  toInt(val: any): number {
    return parseInt(val, 10);
  }

  trackById(index: number, item: Stockitem): number {
    return item.id;
  }

  canSeeAllPrices(): boolean {
    return (
      this.accountTypeService.isAdmin() ||
      this.accountTypeService.isSuperAdmin() ||
      this.accountTypeService.isSubAdmin()
    );
  }
}
